import "@fontsource/inter/300.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "material-symbols/rounded.css";
import "~mangomap-ui/styles/global.css";

import { type EmotionCache } from "@emotion/react";
import { Amplify, type ResourcesConfig } from "aws-amplify";
import GlobalStyles from "@mui/material/GlobalStyles";
import { type NextPage } from "next";
import type { AppProps } from "next/app";
import { DefaultSeo as DefaultNextSeo, type DefaultSeoProps } from "next-seo";
import { OpenGraph } from "next-seo/lib/types";
import NProgress from "nprogress";
import { useEffect } from "react";
import { PartnerAuthProvider } from "~/auth";
import {
	defaultSettings,
	SettingsProvider,
} from "~mangomap-ui/components/settings/settings-context";
import { LocalizationProvider } from "~mangomap-ui/components/localization-provider";
import { ThemeProvider } from "~mangomap-ui/components/theme-provider";
import { Toaster } from "~ui-components/components/atoms/toaster";
import { I18nProvider } from "~utils/providers/i18n-provider";
import { QueryProvider } from "~utils/providers/query-provider";

NProgress.configure({ showSpinner: false });

const commonLinkTags = [
	{ rel: "preconnect", href: "https://fonts.googleapis.com" },
	{ rel: "preconnect", href: "https://fonts.gstatic.com" },
	{ rel: "preconnect", href: "https://dev.visualwebsiteoptimizer.com" },
];

const mangoFaviconTags = [
	{ rel: "icon" as const, href: "/favicon.ico" },
	{
		rel: "apple-touch-icon" as const,
		href: "/icons/apple-touch-icon.png",
		sizes: "180x180",
	},
	{
		rel: "icon" as const,
		type: "image/png",
		sizes: "32x32",
		href: "/icons/favicon-32x32.png",
	},
	{
		rel: "icon" as const,
		type: "image/png",
		sizes: "16x16",
		href: "/icons/favicon-16x16.png",
	},
];

const buildSeoProps = ({
	appName,
	title,
	description,
	faviconTags,
	openGraphImages,
}: {
	appName?: string;
	title?: string;
	description?: string;
	faviconTags: typeof mangoFaviconTags;
	openGraphImages?: OpenGraph["images"];
}): DefaultSeoProps => ({
	...(title ? { title } : {}),
	...(appName
		? { titleTemplate: `%s - ${appName}`, defaultTitle: appName }
		: {}),
	...(description ? { description } : {}),
	dangerouslySetAllPagesToNoFollow: true,
	dangerouslySetAllPagesToNoIndex: true,
	themeColor: "#ff5722",
	canonical: "https://mangomap.com",
	robotsProps: {
		noarchive: true,
		nosnippet: true,
		notranslate: true,
		noimageindex: true,
		maxSnippet: -1,
		maxImagePreview: "large",
		maxVideoPreview: -1,
	},
	additionalMetaTags: [
		...(appName
			? [
					{ name: "application-name", content: appName },
					{ name: "apple-mobile-web-app-title", content: appName },
				]
			: []),
		{ name: "format-detection", content: "telephone=no" },
		{ name: "msapplication-TileColor", content: "#FF5621" },
		{ name: "msapplication-tap-highlight", content: "no" },
		{
			name: "googlebot",
			content:
				"noindex, nofollow, noarchive, nosnippet, noimageindex, notranslate",
		},
		{
			name: "bingbot",
			content:
				"noindex, nofollow, noarchive, nosnippet, noimageindex, notranslate",
		},
	],
	additionalLinkTags: [...faviconTags, ...commonLinkTags],
	openGraph: {
		type: "website",
		url: "https://mangomap.com",
		...(title ? { title } : {}),
		...(description ? { description } : {}),
		...(appName ? { siteName: appName } : {}),
		locale: "en_GB",
		...(openGraphImages ? { images: openGraphImages } : {}),
	},
});

const seoProps: DefaultSeoProps = buildSeoProps({
	appName: "MangoMap Partner",
	title: "MangoMap Partner dashboard",
	description: "MangoMap Partner dashboard",
	faviconTags: mangoFaviconTags,
	openGraphImages: [
		{
			url: "/new-mangomap-assets/mangoGIS.png",
			width: 1182,
			height: 670,
			alt: "Mango GIS",
			type: "image/png",
		},
	],
});

const config: ResourcesConfig = {
	API: {
		GraphQL: {
			endpoint: process.env.APPSYNC_API_URL!,
			defaultAuthMode: "userPool",
		},
	},
	Auth: {
		Cognito: {
			userPoolId: process.env.AUTH_USER_POOL_ID!,
			userPoolClientId: process.env.AUTH_USER_POOL_CLIENT_PARTNER_ID!,
			signUpVerificationMethod: "code",
			loginWith: {
				username: true,
				email: true,
			},
		},
	},
};

Amplify.configure(config, { ssr: true });

export interface CustomAppProps extends AppProps {
	Component: NextPage;
	// emotionCache is passed from _document for Emotion SSR critical CSS extraction
	emotionCache?: EmotionCache;
}

const App = ({
	Component,
	pageProps,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	emotionCache,
	router,
}: CustomAppProps) => {
	useNprogress(router);

	return (
		<QueryProvider>
			<I18nProvider language="en">
				<LocalizationProvider>
					<PartnerAuthProvider>
						<SettingsProvider settings={defaultSettings}>
							<ThemeProvider>
								<DefaultNextSeo {...seoProps} />
								<AppGlobalStyles />
								<Component {...pageProps} />
								<Toaster position="bottom-center" />
							</ThemeProvider>
						</SettingsProvider>
					</PartnerAuthProvider>
				</LocalizationProvider>
			</I18nProvider>
		</QueryProvider>
	);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useNprogress = (router: any) => {
	useEffect(() => {
		const handleRouteStart = () => NProgress.start();
		const handleRouteDone = () => NProgress.done();

		router.events.on("routeChangeStart", handleRouteStart);
		router.events.on("routeChangeComplete", handleRouteDone);
		router.events.on("routeChangeError", handleRouteDone);

		return () => {
			router.events.off("routeChangeStart", handleRouteStart);
			router.events.off("routeChangeComplete", handleRouteDone);
			router.events.off("routeChangeError", handleRouteDone);
		};
	}, [router]);
};

export default App;

const AppGlobalStyles = () => (
	<GlobalStyles
		styles={(t) => `
			#nprogress {
				pointer-events: none;
			}

			#nprogress .bar {
				background: ${t.palette.primary.main};

				position: fixed;
				z-index: 9999;
				top: 0;
				left: 0;

				width: 100%;
				height: 2px;
			}

			#nprogress .peg {
				display: block;
				position: absolute;
				right: 0px;
				width: 100px;
				height: 100%;
				box-shadow: 0 0 10px ${t.palette.primary.main}, 0 0 5px ${t.palette.primary.main};
				opacity: 1;

				-webkit-transform: rotate(3deg) translate(0px, -4px);
				-ms-transform: rotate(3deg) translate(0px, -4px);
				transform: rotate(3deg) translate(0px, -4px);
			}

			#nprogress .spinner {
				display: block;
				position: fixed;
				z-index: 1101;
				top: 15px;
				right: 15px;
			}

			#nprogress .spinner-icon {
				width: 18px;
				height: 18px;
				box-sizing: border-box;

				border: solid 2px transparent;
				border-top-color: ${t.palette.primary.main};
				border-left-color: ${t.palette.primary.main};
				border-radius: 50%;

				-webkit-animation: nprogress-spinner 400ms linear infinite;
				animation: nprogress-spinner 400ms linear infinite;
			}

			.nprogress-custom-parent {
				overflow: hidden;
				position: relative;
			}

			.nprogress-custom-parent #nprogress .spinner,
			.nprogress-custom-parent #nprogress .bar {
				position: absolute;
			}

			@-webkit-keyframes nprogress-spinner {
				0% {
					-webkit-transform: rotate(0deg);
				}
				100% {
					-webkit-transform: rotate(360deg);
				}
			}
			@keyframes nprogress-spinner {
				0% {
					transform: rotate(0deg);
				}
				100% {
					transform: rotate(360deg);
				}
			}
		`}
	/>
);
